import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

export interface FilterMultiSelectOption {
  id: string;
  name: string;
  subDescription?: string;
}

@Component({
  standalone: false,
  selector: 'digicem-filter-multi-select',
  templateUrl: './filter-multi-select.component.html',
})
export class FilterMultiSelectComponent {
  @Input() label = '';
  @Output() changed = new EventEmitter<string[]>();

  protected query = '';
  protected isOpen = false;
  protected filteredOptions: FilterMultiSelectOption[] = [];
  protected selectedOptions: FilterMultiSelectOption[] = [];

  private _options: FilterMultiSelectOption[] = [];
  private _selectedIds: string[] = [];
  private selectedIdSet = new Set<string>();

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @Input()
  set options(value: FilterMultiSelectOption[]) {
    this._options = value;
    this.refreshDerivedState();
  }

  get options(): FilterMultiSelectOption[] {
    return this._options;
  }

  @Input()
  set selectedIds(value: string[]) {
    this._selectedIds = value;
    this.selectedIdSet = new Set(value);
    this.refreshDerivedState();
  }

  get selectedIds(): string[] {
    return this._selectedIds;
  }

  protected isSelected(id: string): boolean {
    return this.selectedIdSet.has(id);
  }

  protected trackById(_index: number, option: FilterMultiSelectOption): string {
    return option.id;
  }

  protected onFieldClick(): void {
    this.isOpen = !this.isOpen;
  }

  protected onQueryInput(value: string): void {
    this.query = value;
    this.isOpen = true;
    this.refreshFilteredOptions();
  }

  protected toggleOption(id: string): void {
    const nextIds = this.isSelected(id)
      ? this.selectedIds.filter(selectedId => selectedId !== id)
      : [...this.selectedIds, id];
    this.changed.emit(nextIds);
  }

  protected removeChip(id: string): void {
    this.changed.emit(this.selectedIds.filter(selectedId => selectedId !== id));
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.query = '';
      this.refreshFilteredOptions();
    }
  }

  private refreshDerivedState(): void {
    this.refreshFilteredOptions();
    this.selectedOptions = this._options.filter(option =>
      this.selectedIdSet.has(option.id),
    );
  }

  private refreshFilteredOptions(): void {
    const term = this.query.trim().toLowerCase();
    this.filteredOptions = term
      ? this._options.filter(
          option =>
            option.name.toLowerCase().includes(term) ||
            (option.subDescription ?? '').toLowerCase().includes(term),
        )
      : this._options;
  }
}
